import { Clock, Mail, Newspaper, Shield, Users } from 'lucide-react';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { trackNewsletterSignup } from '../lib/analytics';

export function NewsletterPage() {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const response = await fetch('https://formspree.io/f/xldlvwak', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setSubscribed(true);
        setEmail('');
        trackNewsletterSignup(); // Track successful newsletter signup
      } else {
        console.error('Failed to subscribe');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Newsletter Signup - AI Literacy Updates | Promptliteracy.ai</title>
        <meta name="description" content="Subscribe to Promptliteracy.ai newsletter for daily AI literacy news, expert insights, educational resources, and weekly digests. Join thousands of educators and AI enthusiasts." />
        
        {/* Open Graph */}
        <meta property="og:title" content="Newsletter Signup - AI Literacy Updates | Promptliteracy.ai" />
        <meta property="og:description" content="Subscribe to Promptliteracy.ai newsletter for daily AI literacy news, expert insights, and educational resources. Join thousands of educators and AI enthusiasts." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://promptliteracy.ai/newsletter" />
        <meta property="og:site_name" content="Promptliteracy.ai" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Newsletter Signup - AI Literacy Updates" />
        <meta name="twitter:description" content="Subscribe for daily AI literacy news, expert insights, and educational resources from Promptliteracy.ai." />
        
        {/* Additional SEO */}
        <meta name="keywords" content="AI literacy newsletter, AI education updates, prompt engineering news, AI research digest, educator newsletter, AI policy updates" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://promptliteracy.ai/newsletter" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Newsletter Signup - Promptliteracy.ai",
            "description": "Subscribe to AI literacy newsletter with daily news, expert insights, and educational resources",
            "url": "https://promptliteracy.ai/newsletter",
            "mainEntity": {
              "@type": "NewsletterService",
              "name": "Promptliteracy.ai Newsletter",
              "description": "Daily AI literacy news and educational resources for educators and enthusiasts",
              "provider": {
                "@type": "Organization",
                "name": "Promptliteracy.ai"
              }
            }
          })}
        </script>
      </Helmet>

      {/* Header Section */}
      <section className="py-16 bg-primary text-white shadow-lg">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Mail className="h-8 w-8" />
              <h1 className="text-4xl font-bold">Newsletter</h1>
            </div>
            <p className="text-xl opacity-90">
              Stay informed with daily AI literacy news, insights, and educational resources
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 bg-primary/3 relative overflow-hidden shadow-lg border-y border-primary/20">
        <div className="absolute inset-0 circuit-pattern opacity-10" />
        <div className="container relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Newsletter Signup */}
          <Card className="mb-12 border-primary/20 bg-primary/5 card-accent">
            <CardContent className="p-8">
              {!subscribed ? (
                <div className="text-center">
                  <h2 className="text-2xl font-bold mb-4">Subscribe to Our Newsletter</h2>
                  <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                    Join thousands of educators, students, and AI enthusiasts who receive our curated content. 
                    We respect your privacy and will never spam you.
                  </p>
                  
                  <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                    <div className="flex gap-2">
                      <Input
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="flex-1"
                      />
                      <Button type="submit" className="btn-tech">
                        Subscribe
                      </Button>
                    </div>
                  </form>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Thank You for Subscribing!</h2>
                  <p className="text-muted-foreground">
                    You'll receive our first newsletter within the next 24 hours. 
                    Welcome to the PromptLiteracy.ai community!
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* What You'll Receive */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <Card className="text-center card-slate card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Newspaper className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Daily News</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Curated AI literacy news and developments from trusted sources
                </p>
              </CardContent>
            </Card>

            <Card className="text-center card-slate card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Expert Insights</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Commentary and analysis from educators and AI researchers
                </p>
              </CardContent>
            </Card>

            <Card className="text-center card-slate card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Weekly Digest</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Weekly roundup of the most important developments
                </p>
              </CardContent>
            </Card>

            <Card className="text-center card-slate card-accent">
              <CardHeader>
                <div className="mx-auto w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg">Privacy First</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  No spam, easy unsubscribe, and your data stays private
                </p>
              </CardContent>
            </Card>
          </div>


          {/* Privacy Information */}
          <Card className="card-accent">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Privacy & Data Protection
              </CardTitle>
            </CardHeader>
            <CardContent className="prose max-w-none">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold mb-3">What We Collect</h3>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Email address for newsletter delivery</li>
                    <li>• Subscription preferences</li>
                    <li>• Basic engagement metrics</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-3">Your Rights</h3>
                  <ul className="space-y-1 text-sm text-muted-foreground">
                    <li>• Unsubscribe anytime with one click</li>
                    <li>• Request data deletion</li>
                    <li>• No sharing with third parties</li>
                  </ul>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-6">
                By subscribing, you agree to receive educational content via email. 
                You can unsubscribe at any time. Read our full{' '}
                <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a>.
              </p>
            </CardContent>
          </Card>
        </div>
        </div>
      </section>
    </>
  );
}